import axios from 'axios';
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

// Base URL. Must end WITHOUT /api/v1 — we append it here so components
// can call `apiClient.get('/portal/...')` and stay path-stable.
//
// Default is an EMPTY string (same-origin relative URL). That means the
// browser POSTs to whatever host served the page:
//   • Production via Cloudflare Tunnel  → https://ljiet.online/api/v1/...
//   • Dev (`npm run dev`)               → http://localhost:5173/api/v1/...
//     and Vite's `server.proxy` (vite.config.ts) forwards /api/* to
//     Django on http://localhost:8000.
//
// Why this matters: a hardcoded `http://localhost:8000` fallback used to
// ship inside the production bundle. On the developer's PC, that string
// happened to point at Django and worked. On a student's phone,
// `localhost` resolves to the phone itself — no Django there — so every
// API call (including Google sign-in) failed. Same-origin avoids that
// entire class of bug.
//
// Override only when the frontend and backend live on different hosts
// (e.g. Vercel + Render). In that case set VITE_API_BASE_URL to the
// absolute backend URL in `frontend/.env`.
const RAW_BASE = import.meta.env.VITE_API_BASE_URL || '';
const API_URL = `${RAW_BASE.replace(/\/$/, '')}/api/v1`;

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});

// ─────────────────────────────────────────────────────────────
// GET response cache + in-flight dedup
//
// Two distinct optimisations that share a key map:
//
//   A. In-flight dedup. When two components mount on the same tick
//      and both call `apiClient.get('/auth/me/')`, only one actual
//      network request is dispatched; the second caller awaits the
//      first promise. Saves the duplicate hop without any TTL.
//
//   B. TTL response cache. For URLs on CACHE_RULES, the response
//      payload is held in memory for `ttlMs` after it lands.
//      Subsequent GETs within the window resolve instantly from
//      the cache, no HTTP round-trip. The student-dashboard fan-
//      out (7 calls per mount → 0 calls within window) is the
//      lever this is built for.
//
// Mutations (POST/PUT/PATCH/DELETE) wipe the cache and broadcast a
// bust signal to sibling tabs. Coarse but correct: TTLs are short
// and a cold refetch after a write is exactly what the user expects.
// Logout clears it too, so a re-login can never see prior-session
// data.
//
// What is intentionally NOT here:
//   • per-user namespacing — clearTokensAndRedirect() wipes on
//     logout, which is the only realistic user-switch path
//   • LRU by access time — Map's insertion-order eviction is good
//     enough at our key cardinality (~dozens of distinct URLs)
//   • staleness across the network — admin/HoD writes can't notify
//     us; the short TTLs (≤5min, mostly ≤30s) bound the window
// ─────────────────────────────────────────────────────────────

interface CacheRule {
  prefix: string;
  ttlMs: number;
}

// Order matters when two rules could match: longer/more specific
// prefixes come first. The lookup walks top-down and takes the
// first hit.
const CACHE_RULES: ReadonlyArray<CacheRule> = [
  { prefix: '/portal/students/me/outcomes/schema/', ttlMs: 5 * 60_000 },
  { prefix: '/auth/me/',                            ttlMs: 60_000 },
  { prefix: '/portal/analytics/',                   ttlMs: 30_000 },
  { prefix: '/portal/students/me/',                 ttlMs: 15_000 },
];

const MAX_CACHE_SIZE = 200;

interface CacheEntry {
  data: unknown;
  expiresAt: number;
}

const responseCache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<AxiosResponse>>();

function ttlForUrl(url: string): number | null {
  for (const rule of CACHE_RULES) {
    if (url.startsWith(rule.prefix)) return rule.ttlMs;
  }
  return null;
}

function makeCacheKey(url: string, params: unknown): string {
  // Params matter — `/heatmap/?year=2025` and `?year=2026` are
  // different responses. Stringify once, deterministically enough
  // for our use (we always pass plain objects from the caller).
  return params ? `${url}::${JSON.stringify(params)}` : url;
}

function pruneCache() {
  while (responseCache.size > MAX_CACHE_SIZE) {
    const oldest = responseCache.keys().next().value;
    if (oldest === undefined) break;
    responseCache.delete(oldest);
  }
}

// Cross-tab cache busting. When tab A POSTs an internship, tab B's
// cached `/internships/` list is now stale; without this, tab B
// could serve a 15-second-old empty list right after the user added
// a row. BroadcastChannel is supported in every browser that also
// has navigator.locks (which we already rely on for JWT refresh),
// so feature-detection is the same boundary.
let bustChannel: BroadcastChannel | null = null;
try {
  if (typeof BroadcastChannel !== 'undefined') {
    bustChannel = new BroadcastChannel('portal-cache-bust');
    bustChannel.addEventListener('message', (e) => {
      if (e.data && e.data.type === 'bust') {
        responseCache.clear();
      }
    });
  }
} catch {
  // Older browsers / private modes that disallow BroadcastChannel.
  // We still cache within-tab; cross-tab staleness is bounded by TTL.
}

function broadcastBust() {
  try {
    bustChannel?.postMessage({ type: 'bust' });
  } catch {
    /* channel might be closed during page unload — ignore */
  }
}

/**
 * Force-clear the cache (and signal sibling tabs to do the same).
 * Components don't normally need to call this — mutations bust
 * automatically — but it's exported for the rare case where a
 * background event (e.g. SSE-style notification) needs to invalidate
 * everything on demand.
 */
export function clearApiCache(): void {
  responseCache.clear();
  inFlight.clear();
  broadcastBust();
}

// Internal config marker so the response interceptor knows which
// requests to write into the cache after they succeed.
type CacheableConfig = InternalAxiosRequestConfig & {
  _cacheKey?: string;
  _cacheTtl?: number;
};

// ─────────────────────────────────────────────────────────────
// Request interceptor — attach access token
// ─────────────────────────────────────────────────────────────
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─────────────────────────────────────────────────────────────
// Request interceptor — cache lookup + in-flight dedup
//
// Registered AFTER the auth interceptor so it runs FIRST (axios
// invokes request interceptors in reverse-registration order).
// That way a cache hit short-circuits the request before we
// bother attaching anything; a cache miss falls through and the
// auth interceptor adds the token as usual.
//
// This function is intentionally synchronous (no awaits): the
// inFlight check + set must be atomic with respect to other JS
// to keep the dedup correct. JS is single-threaded, so a sync
// block from `inFlight.get(key)` to `inFlight.set(key, …)` cannot
// be interrupted by a sibling request's interceptor running
// between them.
// ─────────────────────────────────────────────────────────────
apiClient.interceptors.request.use((rawConfig) => {
  const config = rawConfig as CacheableConfig;
  const method = (config.method ?? 'get').toLowerCase();

  // Mutations bust the cache (and signal sibling tabs) before
  // the network call goes out. Doing it pre-flight is fine: if
  // the mutation fails validation, the worst case is a single
  // cold refetch on the user's next read, and TTLs are short.
  if (method !== 'get') {
    if (responseCache.size > 0) {
      responseCache.clear();
      broadcastBust();
    }
    return config;
  }

  const path = config.url ?? '';
  const ttl = ttlForUrl(path);
  if (!ttl) return config; // not on the allowlist — passthrough

  const key = makeCacheKey(path, config.params);

  // (B) Cache hit. Replace the adapter with one that returns
  // the cached payload synchronously. Axios itself never makes
  // a network call.
  const entry = responseCache.get(key);
  if (entry && entry.expiresAt > Date.now()) {
    // Touch insertion order so this entry doesn't get pruned
    // first when we hit MAX_CACHE_SIZE.
    responseCache.delete(key);
    responseCache.set(key, entry);
    config.adapter = () =>
      Promise.resolve({
        data: entry.data,
        status: 200,
        statusText: 'OK (cache)',
        headers: {},
        config,
      } as AxiosResponse);
    return config;
  }

  // (A) In-flight dedup. Another caller already kicked off the
  // same request — share their promise instead of dispatching
  // a duplicate. The `{ ...r, config }` spread keeps each
  // caller's own config object so axios's per-request hooks
  // (transformResponse etc.) see the right thing.
  const existing = inFlight.get(key);
  if (existing) {
    config.adapter = () => existing.then((r) => ({ ...r, config }));
    return config;
  }

  // First call: stash the key/ttl on the config so the response
  // interceptor below can persist the result, and register the
  // promise eagerly via an adapter wrapper so concurrent siblings
  // can find it. The wrapping happens by replacing config.adapter
  // with a wrapper that calls the original then resolves our
  // shared promise.
  config._cacheKey = key;
  config._cacheTtl = ttl;

  // axios.defaults.adapter is typed as AxiosAdapter | string | string[]
  // (strings are built-in adapter names like 'xhr'). We can only wrap
  // when we have a callable; otherwise let axios resolve the adapter
  // itself and skip the in-flight dedup for this request.
  const candidateAdapter = config.adapter ?? axios.defaults.adapter;
  const originalAdapter =
    typeof candidateAdapter === 'function' ? candidateAdapter : null;
  if (originalAdapter) {
    let resolveFn: (r: AxiosResponse) => void = () => {};
    let rejectFn: (e: unknown) => void = () => {};
    const flightPromise = new Promise<AxiosResponse>((res, rej) => {
      resolveFn = res;
      rejectFn = rej;
    });
    inFlight.set(key, flightPromise);

    config.adapter = async (cfg) => {
      try {
        const r = await originalAdapter(cfg);
        resolveFn(r);
        return r;
      } catch (e) {
        rejectFn(e);
        throw e;
      } finally {
        // Whether success or failure, this slot is no longer
        // in flight. Cache write itself happens in the response
        // interceptor on success only.
        inFlight.delete(key);
      }
    };
  }
  return config;
});

// ─────────────────────────────────────────────────────────────
// Response interceptor — write successful GETs into the cache
//
// Registered before the JWT-refresh interceptor below so it runs
// first on success. (For errors, the refresh interceptor handles
// 401s; we only care about 2xx responses for caching.)
// ─────────────────────────────────────────────────────────────
apiClient.interceptors.response.use((response) => {
  const config = response.config as CacheableConfig;
  if (config._cacheKey && config._cacheTtl) {
    responseCache.set(config._cacheKey, {
      data: response.data,
      expiresAt: Date.now() + config._cacheTtl,
    });
    pruneCache();
  }
  return response;
});

// ─────────────────────────────────────────────────────────────
// Response interceptor — silent JWT refresh with concurrent-request queue
//
// Two layers of de-duplication, because two distinct things go wrong:
//
//   1. Intra-tab race. When N requests in the SAME tab race a 401
//      simultaneously, only the first fires `/auth/token/refresh/`.
//      The rest park in `pendingQueue` and resume once the refresh
//      resolves (or fail together if refresh fails). Without this,
//      five parallel 401s would post the same refresh token five
//      times and SimpleJWT's ROTATE_REFRESH_TOKENS would blacklist
//      it after the first call — the other four 401 a second time.
//
//   2. Cross-tab race. The intra-tab lock is module-scoped, so it
//      doesn't help when a student has three tabs open and ALL of
//      them 401 at the same wall-clock moment (typical when access
//      token expires and every tab is polling /auth/me). Each tab
//      grabs its own intra-tab lock, posts its own refresh, and the
//      second + third posts use a refresh token that was just
//      rotated and blacklisted — so two tabs out of three log out.
//
//      Layer two is `navigator.locks.request('jwt-refresh', ...)`,
//      which serializes lock acquisition across every tab on this
//      origin. Inside the lock we re-read `access_token` from
//      localStorage; if a sibling tab already refreshed it while
//      we were waiting, we skip the network call entirely and
//      reuse the new token. One POST per browser per expiry,
//      regardless of how many tabs are open.
// ─────────────────────────────────────────────────────────────

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

interface QueuedRequest {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}

let isRefreshing = false;
let pendingQueue: QueuedRequest[] = [];

function flushQueue(error: unknown, token: string | null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error || !token) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
}

function clearTokensAndRedirect() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  // Wipe the API cache so a different user logging in next never
  // sees a fragment of the previous session's data — the cache is
  // keyed by URL only, not by user, and short TTLs alone aren't
  // sufficient if the next login happens within seconds.
  responseCache.clear();
  inFlight.clear();
  broadcastBust();
  // Avoid redirect loops when the login page itself 401s.
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

// Cross-tab refresh primitive. Wraps `fn` in a named Web Lock so that
// even when multiple tabs of this origin call it concurrently, only one
// runs at a time; the rest queue at the browser level. `fn` itself is
// responsible for the smart-skip — re-reading localStorage after the
// lock is acquired so it can no-op when a sibling already refreshed.
//
// Falls back to a plain call when navigator.locks is unavailable
// (very old Safari / non-secure-context dev hosts). In that case we
// still have intra-tab single-flight, just not cross-tab serialization.
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return navigator.locks.request('jwt-refresh', fn) as Promise<T>;
  }
  return fn();
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequest | undefined;
    const status = error.response?.status;

    // Not a 401, no config to retry, or already retried once — bail.
    if (status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Refresh endpoint itself returning 401 — refresh token is invalid/expired.
    if (originalRequest.url?.includes('/auth/token/refresh/')) {
      clearTokensAndRedirect();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // Another request is already refreshing — queue up.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (newToken: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            resolve(apiClient(originalRequest as AxiosRequestConfig));
          },
          reject,
        });
      });
    }

    // We're the first in this tab to hit 401 — take the intra-tab lock.
    // Capture the access token we *expected* to be valid before queueing
    // for the cross-tab Web Lock, so we can detect a sibling-tab refresh
    // and skip our own redundant POST.
    isRefreshing = true;
    const accessTokenAtFailure = localStorage.getItem('access_token');

    try {
      const newAccessToken = await withRefreshLock(async () => {
        // Re-read AFTER lock acquisition. If a sibling tab refreshed
        // while we were queued at the Web Lock, access_token in storage
        // will differ from the one our failing request used — that's
        // the new valid token, no network call needed.
        const currentAccess = localStorage.getItem('access_token');
        if (currentAccess && currentAccess !== accessTokenAtFailure) {
          return currentAccess;
        }
        // We're the first across all tabs (or alone). Re-read the
        // refresh token now too — a sibling that lost the race may
        // have rotated it just before us.
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('NO_REFRESH_TOKEN');
        }
        const { data } = await axios.post(`${API_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        });
        localStorage.setItem('access_token', data.access);
        // SimpleJWT ROTATE_REFRESH_TOKENS also returns a fresh refresh token.
        if (data.refresh) localStorage.setItem('refresh_token', data.refresh);
        return data.access as string;
      });

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      }
      flushQueue(null, newAccessToken);
      return apiClient(originalRequest as AxiosRequestConfig);
    } catch (refreshError) {
      flushQueue(refreshError, null);
      clearTokensAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);
